import type { ComponentType, SVGProps } from 'react'
import { MicrophoneIcon, QueueListIcon } from '@heroicons/react/24/outline'
import { navigateTo } from '@/lib/navigation'

interface TabDef {
  label: string
  path: string
  isActive: (pathname: string) => boolean
  Icon: ComponentType<SVGProps<SVGSVGElement>>
}

const TABS: TabDef[] = [
  {
    label: 'Record',
    path: '/',
    isActive: (pathname) => !pathname.startsWith('/submissions'),
    Icon: MicrophoneIcon,
  },
  {
    label: 'Submissions',
    path: '/submissions',
    isActive: (pathname) => pathname.startsWith('/submissions'),
    Icon: QueueListIcon,
  },
]

interface BottomNavProps {
  pathname: string
}

/**
 * Fixed mobile bottom tab bar switching between the Record and Submissions
 * screens. Uses the app's manual router (navigateTo dispatches `app:navigate`),
 * and reads the current pathname (passed from App) to mark the active tab.
 */
export function BottomNav({ pathname }: BottomNavProps): JSX.Element {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-navy-light bg-navy-dark/95 pb-[env(safe-area-inset-bottom)] backdrop-blur"
      role="tablist"
      aria-label="Primary"
    >
      <div className="mx-auto flex w-full max-w-md">
        {TABS.map(({ label, path, isActive, Icon }) => {
          const active = isActive(pathname)
          return (
            <button
              key={path}
              type="button"
              role="tab"
              aria-selected={active}
              aria-current={active ? 'page' : undefined}
              aria-label={label}
              onClick={() => navigateTo(path)}
              className={`flex flex-1 flex-col items-center gap-1 py-2 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-neon-cyan ${
                active ? 'text-neon-cyan' : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <Icon className="h-6 w-6" aria-hidden="true" />
              <span>{label}</span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}
